import { Button, Spinner } from "@heroui/react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import type { ReactNode } from "react";
import { LimeSelect } from "@/Components/NextBase/LimeSelect";
import { LimeSwitch } from "@/Components/NextBase/LimeSwitch";
import LimitationsSection from "@/pages/Dashboard/Subscriptions/components/tabs/LimitationsSection";
import type { LimitationTimeframe } from "@/server-types";

export type PlanLimitation = {
  timeframe: LimitationTimeframe;
  limitAmount: number;
};

export type PlanServiceDraft = {
  serviceId: number;
  limitations: PlanLimitation[];
  limitEntries?: boolean;
};

export type SelectableService = {
  serviceId: number;
  name: string;
  tagNameLocalized?: string | null;
};

type CustomSubscriptionServicesEditorProps = {
  heading: ReactNode;
  services: PlanServiceDraft[];
  onChange: (services: PlanServiceDraft[]) => void;
  availableServices: SelectableService[];
  isLoading: boolean;
};

export function CustomSubscriptionServicesEditor({
  heading,
  services,
  onChange,
  availableServices,
  isLoading,
}: CustomSubscriptionServicesEditorProps) {
  function updateService(index: number, patch: Partial<PlanServiceDraft>) {
    onChange(
      services.map((service, serviceIndex) =>
        serviceIndex === index ? { ...service, ...patch } : service,
      ),
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        {heading}
        <Button
          data-identifier="custom-subscription-add-service-btn"
          size="sm"
          onPress={() =>
            onChange([
              ...services,
              { serviceId: -1, limitations: [], limitEntries: false },
            ])
          }
        >
          <Trans>Dodaj storitev</Trans>
        </Button>
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-4">
          <Spinner />
        </div>
      )}

      {services.length === 0 && (
        <p className="text-gray-600 text-sm">
          <Trans>Ni dodanih storitev.</Trans>
        </p>
      )}

      {services.map((subscriptionService, index) => (
        <div key={index} className="flex flex-col gap-3 rounded-lg border p-3">
          <LimeSelect
            data-identifier={`custom-subscription-service-select-${index}`}
            label={t`Storitev`}
            labelPlacement="outside"
            placeholder={t`Izberi storitev`}
            selectedKeys={
              subscriptionService.serviceId > 0
                ? [String(subscriptionService.serviceId)]
                : []
            }
            onSingleItemChange={(value) => {
              const selected = availableServices.find(
                (service) => service.serviceId === parseInt(String(value)),
              );
              if (selected) {
                updateService(index, { serviceId: selected.serviceId });
              }
            }}
            items={availableServices.map((service) => ({
              key: service.serviceId.toString(),
              label: service.name,
              sectionName: service.tagNameLocalized ?? undefined,
            }))}
          />

          {subscriptionService.serviceId > 0 && (
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-2">
                <LimeSwitch
                  isSelected={!!subscriptionService.limitEntries}
                  size="sm"
                  isCompact
                  onValueChange={(value) => {
                    const limitations = (() => {
                      if (!value) return [];
                      return subscriptionService.limitations.length > 0
                        ? subscriptionService.limitations
                        : [{ timeframe: "all" as const, limitAmount: 1 }];
                    })();
                    updateService(index, { limitEntries: value, limitations });
                  }}
                  title={t`Omeji vstope`}
                />
              </div>
              {!!subscriptionService.limitEntries && (
                <LimitationsSection
                  parentIndex={index}
                  limitations={subscriptionService.limitations}
                  onAddLimitation={() =>
                    updateService(index, {
                      limitations: [
                        ...subscriptionService.limitations,
                        { timeframe: "all", limitAmount: 1 },
                      ],
                    })
                  }
                  onRemoveLimitation={(_, limitationIndex) =>
                    updateService(index, {
                      limitations: subscriptionService.limitations.filter(
                        (_limitation, i) => i !== limitationIndex,
                      ),
                    })
                  }
                  onUpdateLimitation={(_, limitationIndex, field, value) =>
                    updateService(index, {
                      limitations: subscriptionService.limitations.map(
                        (limitation, i) =>
                          i === limitationIndex
                            ? { ...limitation, [field]: value }
                            : limitation,
                      ),
                    })
                  }
                />
              )}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              size="sm"
              variant="light"
              onPress={() =>
                onChange(services.filter((_service, i) => i !== index))
              }
            >
              <Trans>Odstrani</Trans>
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}
